import React, { useState, useEffect } from 'react';
import { achievementsAPI } from '../services/api';
import { Achievement, UserAchievement } from '../types';
import { API_BASE } from '../utils/fileUtils';
import PageHeader from '../components/PageHeader';
import '../styles/global.css';

const Achievements: React.FC = () => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [userAchievements, setUserAchievements] = useState<UserAchievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [achievementsData, userAchievementsData] = await Promise.all([
        achievementsAPI.getAvailableAchievements(), // Используем новый endpoint
        achievementsAPI.getMyProgress()
      ]);
      setAchievements(achievementsData);
      setUserAchievements(userAchievementsData);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Ошибка загрузки данных';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const getUserProgress = (achievementId: number) => {
    const userAchievement = userAchievements.find(ua => ua.achievement_id === achievementId);
    return userAchievement || { percentage_completed: 0, is_completed: false };
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="container">
      <PageHeader title="Мои достижения" />

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
        {achievements.map(achievement => {
          const progress = getUserProgress(achievement.id);

          return (
            <div key={achievement.id} className="card">
              <div className="text-center mb-3">
                {achievement.icon_path ? (
                  <img
                    src={`${API_BASE}/uploads/${achievement.icon_path}`}
                    alt={achievement.title}
                    style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: 'var(--primary-orange)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-inverse)',
                    fontSize: '24px',
                    margin: '0 auto',
                    fontWeight: 'bold'
                  }}>
                    A
                  </div>
                )}
              </div>

              <h3 style={{ color: 'var(--primary-orange)', textAlign: 'center', marginBottom: '0.75rem' }}>
                {achievement.title}
              </h3>

              <p className="text-center mb-3" style={{ color: 'var(--text-secondary)' }}>
                {achievement.description}
              </p>

              <div className="progress-bar mb-2">
                <div
                  className="progress-fill"
                  style={{
                    width: `${progress.percentage_completed}%`,
                    backgroundColor: progress.is_completed ? 'var(--success)' : 'var(--primary-orange)'
                  }}
                />
              </div>

              <div className="text-center">
                <p style={{ margin: '0', fontWeight: 'bold' }}>
                  {progress.percentage_completed.toFixed(1)}% выполнено
                </p>
                <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                  Требуется: {achievement.required_percentage}%
                </p>
                {progress.is_completed && (
                  <div style={{
                    color: 'var(--success)',
                    fontWeight: 'bold',
                    marginTop: '10px'
                  }}>
                    ЗАВЕРШЕНО
                    {progress.completed_at && (
                      <div style={{ fontSize: '12px', fontWeight: 'normal' }}>
                        {new Date(progress.completed_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {achievements.length === 0 && (
        <div className="text-center p-5" style={{ color: 'var(--text-muted)' }}>
          <h3>Пока нет доступных достижений</h3>
          <p>Администратор еще не добавил достижения в систему</p>
        </div>
      )}
    </div>
  );
};

export default Achievements;